const makerjs = require('makerjs')

import { layerNames } from '@/js/cad/CadLayerNaming.js'

export default class HalfQuartArcHandle {
  constructor(handleWidth, handleType, joined = false) {
    this.models = {}

    this.handleWidth = parseInt(handleWidth)
    this.handleType = handleType
    this.joined = joined
    this.handlePocketOffset = 19.5
    this.insertInset = 0.5
    this.shadowOffset = 1.5

    this.screwHoleRadius = 1.5
    this.screwHoleInsetX = 6.5
    this.baseScrewInsetY = 6.5 // the amount for most left and right screw hole to be bumped in the Y direction

    switch (handleType) {
      case 'halfArc':
      case 'lrgHalfArc':
        this.insertCircleWidth = this.handleWidth / 2 + this.handlePocketOffset - this.insertInset
        this.subtractInsertRecWidth = (this.handleWidth / 2 + this.handlePocketOffset - this.insertInset + 2) * 2
        this.subtractInsertRecHeight =
          this.handleWidth / 2 + this.handlePocketOffset - this.insertInset + this.shadowOffset
        this.subtractInsertRecPosX = -this.handleWidth / 2 - this.handlePocketOffset + this.insertInset - 2
        this.subtractInsertRecPosY = -this.shadowOffset
        this.firstHoleOriginX =
          -this.handleWidth / 2 + this.handlePocketOffset - this.insertInset - this.screwHoleInsetX
        this.firstHoleOriginY = this.shadowOffset
        this.arcRadius = this.handleWidth / 2 + this.handlePocketOffset - this.insertInset - this.screwHoleInsetX
        this.arcAngle = 180
        this.screwHoleCount = 4
        break
      case 'quartArc':
      case 'lrgQuartArc':
        this.insertCircleWidth = this.handleWidth + this.handlePocketOffset - this.insertInset
        this.subtractInsertRecWidth = this.handleWidth + this.handlePocketOffset - this.insertInset - this.shadowOffset
        this.subtractInsertRecHeight = this.handleWidth + this.handlePocketOffset - this.insertInset
        this.subtractInsertRecPosX = this.shadowOffset
        this.subtractInsertRecPosY =
          -(this.handleWidth + this.handlePocketOffset - this.insertInset) - this.shadowOffset
        this.firstHoleOriginX = -(this.handleWidth + this.handlePocketOffset - this.insertInset) - this.screwHoleInsetX
        this.firstHoleOriginY = this.shadowOffset
        this.arcRadius = this.handleWidth + this.handlePocketOffset - this.insertInset - this.screwHoleInsetX
        this.arcAngle = -90
        this.screwHoleCount = 3
        break
    }

    if (this.joined) {
      this.generateJoinedHalfQuartArcHandle()
    } else {
      this.generateHalfQuartArcHandle()
    }
  }

  buildScrewHoles() {
    const isHalf = this.handleType == 'halfArc' || this.handleType == 'lrgHalfArc'

    const firstHole = new makerjs.models.Ellipse(this.screwHoleRadius, this.screwHoleRadius)
    firstHole.origin = [this.firstHoleOriginX, this.firstHoleOriginY]
    const screwGuideArc = new makerjs.paths.Arc([0, 0], this.arcRadius, this.arcAngle, 0)

    const screwRow = makerjs.layout.cloneToRow(firstHole, this.screwHoleCount, 0)
    const holes = makerjs.layout.childrenOnPath(screwRow, screwGuideArc, 0.5, false, true)

    if (isHalf) {
      // bump first + last hole
      makerjs.model.moveRelative(holes.models[0], [0, -this.baseScrewInsetY + this.shadowOffset])
      makerjs.model.moveRelative(holes.models[this.screwHoleCount - 1], [0, -this.baseScrewInsetY + this.shadowOffset])
    } else {
      // bump first + last hole
      makerjs.model.moveRelative(holes.models[0], [this.baseScrewInsetY - this.shadowOffset, 0])
      makerjs.model.moveRelative(holes.models[this.screwHoleCount - 1], [0, -this.baseScrewInsetY + this.shadowOffset])
    }
    return holes
  }

  generateJoinedHalfQuartArcHandle() {
    const isHalf = this.handleType == 'halfArc' || this.handleType == 'lrgHalfArc'
    const R = this.insertCircleWidth

    // ----- outline -----
    if (isHalf) {
      // full circle, seam running through its centre
      this.models.handleInsert = {
        layer: layerNames.insertCutout.label,
        paths: {
          topArc: { type: 'arc', origin: [0, 0], radius: R, startAngle: 0, endAngle: 180 },
          botArc: { type: 'arc', origin: [0, 0], radius: R, startAngle: 180, endAngle: 360 }
        }
      }
    } else {
      const insertCircle = new makerjs.models.Ellipse(R, R)
      const subtractInsertRec = new makerjs.models.Rectangle(R - this.shadowOffset + 2, (R + 2) * 2)
      subtractInsertRec.origin = [this.shadowOffset, -(R + 2)]
      this.models.handleInsert = makerjs.model.combine(insertCircle, subtractInsertRec, true, false, true, false)
      this.models.handleInsert.layer = layerNames.insertCutout.label
    }

    // ----- screw holes: build one front, mirror across the seam for the other -----
    const holes = this.buildScrewHoles()
    const mirrorHoles = makerjs.model.mirror(holes, false, true)

    this.models.screwHoles = holes
    this.models.screwHoles.layer = layerNames.insertCountersink.label
    this.models.mirrorHoles = mirrorHoles
    this.models.mirrorHoles.layer = layerNames.insertCountersink.label

    // ----- shadow line scribed down the centre of the seam band -----
    this.models.shadowLine = {
      layer: layerNames.shadowLine.label,
      paths: {
        seam: { type: 'line', origin: [(isHalf ? -R : 0) - 2, 0], end: [R + 2, 0] }
      }
    }
  }

  generateHalfQuartArcHandle() {
    // combine(modelA, modelB, A inside B, A outside B, B inside A, B outside A)
    // handle outline
    const insertCircle = new makerjs.models.Ellipse(this.insertCircleWidth, this.insertCircleWidth)

    const subtractInsertRec = new makerjs.models.Rectangle(this.subtractInsertRecWidth, this.subtractInsertRecHeight)

    subtractInsertRec.origin = [this.subtractInsertRecPosX, this.subtractInsertRecPosY]
    if (this.handleType == 'halfArc' || this.handleType == 'lrgHalfArc') {
      this.models.handleInsert = makerjs.model.combine(insertCircle, subtractInsertRec, false, true, true, false)
    } else if (this.handleType == 'quartArc' || this.handleType == 'lrgQuartArc') {
      this.models.handleInsert = makerjs.model.combine(insertCircle, subtractInsertRec, true, false, true, false)
    }
    this.models.handleInsert.layer = layerNames.insertCutout.label

    // handle screws
    const firstHole = new makerjs.models.Ellipse(this.screwHoleRadius, this.screwHoleRadius)
    firstHole.origin = [this.firstHoleOriginX, this.firstHoleOriginY]
    // screw guide arc
    const screwGuideArc = new makerjs.paths.Arc([0, 0], this.arcRadius, this.arcAngle, 0)

    // copy and spread over arc
    let screwRow = makerjs.layout.cloneToRow(firstHole, this.screwHoleCount, 0)
    // layout on the childrenOnPath(clones, guide, y-center, not reversed, contained)
    this.models.screwHoles = makerjs.layout.childrenOnPath(screwRow, screwGuideArc, 0.5, false, true)
    if (this.handleType == 'halfArc' || this.handleType == 'lrgHalfArc') {
      //bump first hole
      makerjs.model.moveRelative(this.models.screwHoles.models[0], [0, -this.baseScrewInsetY + this.shadowOffset])
      //bump last hole
      makerjs.model.moveRelative(this.models.screwHoles.models[this.screwHoleCount - 1], [
        0,
        -this.baseScrewInsetY + this.shadowOffset
      ])
    } else if (this.handleType == 'quartArc' || this.handleType == 'lrgQuartArc') {
      //bump first hole
      makerjs.model.moveRelative(this.models.screwHoles.models[0], [this.baseScrewInsetY - this.shadowOffset, 0])
      //bump last hole
      makerjs.model.moveRelative(this.models.screwHoles.models[this.screwHoleCount - 1], [
        0,
        -this.baseScrewInsetY + this.shadowOffset
      ])
    }
    this.models.screwHoles.layer = layerNames.insertCountersink.label
  }
}
